import * as Sentry from "@sentry/react";
import * as env from "../config/env";

const reportedErrors = new WeakSet();
const CHUNK_RELOAD_AT_KEY = "simpary:chunk-reload-at";
const CHUNK_RELOAD_WINDOW_MS = 10000;

export function isChunkLoadError(error) {
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w.-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

export const pageReload = {
  run() {
    window.location.reload();
  },
};

export function reloadPage() {
  pageReload.run();
}

/**
 * After a deploy, an open tab can request a JS chunk that no longer exists.
 * Reload once so the browser picks up the new index. A second failure inside
 * the window shows the fallback instead of looping.
 */
export function reloadOnceForChunkError(error) {
  if (typeof window === "undefined" || !isChunkLoadError(error)) return false;
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY) || 0);
    const now = Date.now();
    if (Number.isFinite(last) && now - last < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(now));
  } catch {
    return false;
  }
  pageReload.run();
  return true;
}

export function reportRenderError(error, errorInfo, boundaryName) {
  if (error && typeof error === "object") {
    if (reportedErrors.has(error)) return;
    try {
      reportedErrors.add(error);
    } catch {
      /* non-object throwables are reported every time */
    }
  }

  if (env.SENTRY_DSN) {
    const componentStack = errorInfo?.componentStack || "";
    if (typeof Sentry.captureReactException === "function") {
      Sentry.captureReactException(error, { componentStack });
    } else {
      Sentry.captureException(error, {
        extra: { componentStack, boundary: boundaryName || "" },
      });
    }
    return;
  }

  const label = boundaryName || "Render error";
  console.error(`${label} caught:`, error, errorInfo);
}
